import * as Sentry from '@sentry/browser'
import { type Event, EventSourcePolyfill } from 'event-source-polyfill'

import { env } from '@/env'
import { api, handleUnauthorizedSession } from '@/lib/axios'
import { SseFetchTransport } from '@/lib/sse-fetch-transport'
import type { OrderRealtimeChunkType } from '@/types/order-realtime.types'
import { jitteredBackoffDelay } from '@/utils/jittered-backoff-delay'
import { safeStringifyErrorEvent } from '@/utils/safe-stringify-error-event'

type OrdersListener = (orders: OrderRealtimeChunkType[]) => void

type SubscribeRealtimeOrdersParams = {
  merchantIds: string[]
  statuses?: string[]
  onOrders: OrdersListener
  onError?: (error: Event) => void
}

type RealtimeOrdersConnection = {
  key: string
  eventSource: EventSourcePolyfill
  merchantIds: string[]
  statuses: string[]
  listeners: Set<OrdersListener>
  errorListeners: Set<(error: Event) => void>
  retryAttempt: number
  retryTimer: ReturnType<typeof setTimeout> | null
  consecutiveTransportFailures: number
  openedAt: number | null
}

const CLOSE_DELAY_MS = 150
const TRANSPORT_FAILURES_BEFORE_REPORT = 5
// The backend sends a snapshot on every connect, so receiving orders does not
// prove the connection is healthy; staying up this long does.
const STABLE_CONNECTION_MS = 60000
const MAX_RETRY_DELAY_MS = 30000
const ONLINE_RECONNECT_JITTER_MS = 3000
const connections = new Map<string, RealtimeOrdersConnection>()
const closeTimers = new Map<string, ReturnType<typeof setTimeout>>()

function getConnectionKey(merchantIds: string[], statuses: string[]) {
  return `${merchantIds.join(',')}|${statuses.join(',')}`
}

function buildOrdersUrl(merchantIds: string[], statuses: string[]) {
  const params = new URLSearchParams()
  params.set('merchants', merchantIds.join(','))

  if (statuses.length > 0) {
    params.set('status', statuses.join(','))
  }

  return `${env.VITE_API_URL}/orders?${params.toString()}`
}

function notifyListeners(
  connection: RealtimeOrdersConnection,
  orders: OrderRealtimeChunkType[]
) {
  connection.listeners.forEach((listener) => {
    listener(orders)
  })
}

// A non-200/wrong-content-type response on connect (auth hiccup, 5xx, gateway
// blip) makes the polyfill call its internal close(), which clears its retry
// timer for good — the stream then stays dead until something external
// reopens it. On a dropped connection the polyfill does retry, but with the
// headers it was built with, so the token may have expired by then. This
// schedules our own bounded retry, which reads the current token on reopen.
function scheduleReconnect(connection: RealtimeOrdersConnection) {
  if (connection.retryTimer) return

  const delay = jitteredBackoffDelay(connection.retryAttempt, MAX_RETRY_DELAY_MS)
  connection.retryAttempt += 1

  connection.retryTimer = setTimeout(() => {
    connection.retryTimer = null

    if (!connections.has(connection.key) || connection.listeners.size === 0) {
      return
    }

    connection.eventSource.close()
    connection.eventSource = openEventSource(connection)
  }, delay)
}

// Closing right away cancels the polyfill's own retry, so the only reconnect
// is ours, with a fresh token.
function reconnectWithFreshToken(connection: RealtimeOrdersConnection) {
  connection.eventSource.close()
  scheduleReconnect(connection)
}

function getConnectionAgeMs(connection: RealtimeOrdersConnection) {
  return connection.openedAt ? Date.now() - connection.openedAt : null
}

// A connection that stayed up ended a streak: the next drop starts a new one,
// with a short retry delay again.
function resetRetryIfStable(connection: RealtimeOrdersConnection) {
  const connectionAgeMs = getConnectionAgeMs(connection)
  if (connectionAgeMs === null || connectionAgeMs < STABLE_CONNECTION_MS) return

  connection.retryAttempt = 0
  connection.consecutiveTransportFailures = 0
}

// Chrome rejects the stream body read with "TypeError: network error" when
// the connection drops mid-stream (flaky wifi, network suspend, a socket
// closed without a clean end). That is routine on the merchant tablets, so a
// single drop is only a breadcrumb; a streak of drops while online is
// reported once.
function handleTransportFailure(
  connection: RealtimeOrdersConnection,
  error: Error
) {
  const connectionAgeMs = getConnectionAgeMs(connection)
  resetRetryIfStable(connection)

  Sentry.addBreadcrumb({
    category: 'sse',
    level: 'warning',
    message: 'Realtime orders stream dropped',
    data: { error: error.message, connectionAgeMs },
  })

  connection.consecutiveTransportFailures += 1

  if (
    connection.consecutiveTransportFailures ===
      TRANSPORT_FAILURES_BEFORE_REPORT &&
    navigator.onLine
  ) {
    Sentry.captureException(new Error('Realtime orders stream keeps dropping'), {
      level: 'warning',
      fingerprint: ['realtime-orders-stream-dropping'],
      extra: {
        consecutiveFailures: connection.consecutiveTransportFailures,
        lastError: error.message,
        connectionAgeMs,
      },
    })
  }

  reconnectWithFreshToken(connection)
}

function openEventSource(
  connection: RealtimeOrdersConnection
): EventSourcePolyfill {
  connection.openedAt = null

  const eventSource = new EventSourcePolyfill(
    buildOrdersUrl(connection.merchantIds, connection.statuses),
    {
      withCredentials: true,
      headers: {
        Authorization: api.defaults.headers.Authorization as string,
      },
      heartbeatTimeout: 310000, // server sends a heartbeat every 300000ms; stay above that so we don't reconnect on a heartbeat that's simply in flight
      Transport: SseFetchTransport,
    }
  )

  eventSource.addEventListener('open', () => {
    connection.openedAt = Date.now()
  })

  eventSource.addEventListener('orders', (event: Event) => {
    const response = event as Event & { data: string }
    if (!response.data) return

    const orders: OrderRealtimeChunkType[] = JSON.parse(response.data)
    notifyListeners(connection, orders)
  })

  eventSource.addEventListener('error', (event: Event) => {
    const errorEvent = event as Event & {
      error?: Error
      status?: number
      statusText?: string
    }

    // `.error` means the transport failed: a dropped connection or the
    // polyfill's own heartbeat timeout ("Reconnecting").
    if (errorEvent.error) {
      handleTransportFailure(connection, errorEvent.error)
      return
    }

    // No `.error` means the polyfill's onStart rejected the response itself
    // (non-200 status or wrong Content-Type) and already called close().
    if (errorEvent.status === 401) {
      handleUnauthorizedSession()
      return
    }

    // No status at all (not a bad HTTP response, not a heartbeat timeout)
    // means the backend just closed the connection — per the backend team
    // this is a routine, expected connection expiry, not a failure.
    // Reconnect quietly instead of paging Sentry on every occurrence.
    if (errorEvent.status === undefined) {
      resetRetryIfStable(connection)
      reconnectWithFreshToken(connection)
      return
    }

    Sentry.captureException(
      new Error(
        `Realtime orders stream error: HTTP ${errorEvent.status} ${errorEvent.statusText ?? ''}`.trim()
      ),
      { extra: { errorEvent: safeStringifyErrorEvent(errorEvent) } }
    )
    connection.errorListeners.forEach((listener) => listener(event))
    scheduleReconnect(connection)
  })

  return eventSource
}

function createConnection(
  key: string,
  merchantIds: string[],
  statuses: string[]
): RealtimeOrdersConnection {
  const connection: RealtimeOrdersConnection = {
    key,
    eventSource: null as unknown as EventSourcePolyfill,
    merchantIds,
    statuses,
    listeners: new Set(),
    errorListeners: new Set(),
    retryAttempt: 0,
    retryTimer: null,
    consecutiveTransportFailures: 0,
    openedAt: null,
  }

  connection.eventSource = openEventSource(connection)

  connections.set(key, connection)
  return connection
}

function scheduleConnectionClose(key: string) {
  const existingTimer = closeTimers.get(key)
  if (existingTimer) {
    clearTimeout(existingTimer)
  }

  const timer = setTimeout(() => {
    const connection = connections.get(key)
    if (!connection || connection.listeners.size > 0) {
      closeTimers.delete(key)
      return
    }

    if (connection.retryTimer) {
      clearTimeout(connection.retryTimer)
      connection.retryTimer = null
    }

    connection.eventSource.close()
    connections.delete(key)
    closeTimers.delete(key)
  }, CLOSE_DELAY_MS)

  closeTimers.set(key, timer)
}

function cancelScheduledClose(key: string) {
  const timer = closeTimers.get(key)
  if (!timer) return

  clearTimeout(timer)
  closeTimers.delete(key)
}

function subscribeRealtimeOrders({
  merchantIds,
  statuses = [],
  onOrders,
  onError,
}: SubscribeRealtimeOrdersParams): () => void {
  const key = getConnectionKey(merchantIds, statuses)
  cancelScheduledClose(key)

  let connection = connections.get(key)
  if (!connection) {
    connection = createConnection(key, merchantIds, statuses)
  }

  connection.listeners.add(onOrders)
  if (onError) {
    connection.errorListeners.add(onError)
  }

  return () => {
    connection.listeners.delete(onOrders)
    if (onError) {
      connection.errorListeners.delete(onError)
    }

    if (connection.listeners.size === 0) {
      scheduleConnectionClose(key)
    }
  }
}

// setTimeout/setInterval get throttled in background tabs, and OS-level
// network suspend (device sleep, ERR_NETWORK_IO_SUSPENDED) kills the
// underlying connection outright, well before the polyfill's own
// heartbeatTimeout notices. Since each 'orders' event is a full snapshot
// (not a delta), forcing a reconnect is a cheap way to pull a fresh
// snapshot instead of waiting for a throttled/dead heartbeat to catch up.
function reconnectAllConnections() {
  connections.forEach((connection) => {
    if (connection.listeners.size === 0) return

    if (connection.retryTimer) {
      clearTimeout(connection.retryTimer)
      connection.retryTimer = null
    }
    connection.retryAttempt = 0

    connection.eventSource.close()
    connection.eventSource = openEventSource(connection)
  })
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    reconnectAllConnections()
  })
}

// 'online' catches the case the tab was already foregrounded through an OS
// sleep/resume (no visibilitychange fires then) but the connection still
// died from the network suspend, e.g. ERR_NETWORK_IO_SUSPENDED in prod.
// The delay is deliberate: 'online' fires on every affected device at
// roughly the same moment (shared wifi blip, backend redeploy), so
// reconnecting immediately turns a fleet of devices into a synchronized
// burst of fresh connections against the backend. Spreading it over a
// short random window avoids that thundering herd.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    setTimeout(reconnectAllConnections, Math.random() * ONLINE_RECONNECT_JITTER_MS)
  })
}

export { subscribeRealtimeOrders }
